/**
 * @fileoverview "Advanced styles" (plan B3 option B, Stages 2-3): additive representations, a
 * color, a transparency and a cartoon type per group of atoms, for either 3D viewer. It reads and
 * writes BMaps' style model (AtomGroupStyle.reps / .color / .transparency / .cartoonType) through
 * UserActions, so a change is a real BMaps style: kept through redraws and viewer switches, and
 * saved with sessions.
 *
 * Rows are BMaps' own groups: All, each protein chain, each displayed compound, and one row each
 * for waters, fragments and other hetero groups (a row covers all its groups). Each chain has two
 * sub-rows, its main chain and its side chains (Stage 3c, AtomGroupStyle.scopedReps): a part
 * ticked there draws its own reps; a part never ticked draws the chain's. A group without a
 * rep set shows the representations of the single style it has now - its custom style or the
 * view's default - so the table starts from what is on screen.
 *
 * No React here; the app's objects are injected so tests can use stand-ins.
 */

import { AtomGroupTypes } from 'BMapsSrc/model/atomgroups';
import {
    RepNames, RepSetTransparencies, RepSetCartoonTypes, repsForMolStyle, TransparentRepNames,
    transparencyByRep,
} from 'BMapsSrc/style_manager';
import {
    ColorChain, DefaultColorCode, RainbowColorCode, ElementColorCode, SecondaryStructureColorCode,
} from 'BMapsSrc/themes';

/** Columns, in style_manager.RepNames order. */
export const AdvancedStyleReps = RepNames.map((id) => ({
    id, label: id[0].toUpperCase() + id.slice(1),
}));

/**
 * Color choices: BMaps' own palette (the group style menu's), 'default' first, then the schemes
 * display_mgr resolves: Rainbow (chains N -> C), by element (gray carbons, any group), by
 * secondary structure (chains; other groups keep their colors).
 */
export const AdvancedStyleColors = [
    ...Object.entries(ColorChain).map(([label, value]) => ({ label, value })),
    { label: 'Rainbow (N to C)', value: RainbowColorCode },
    { label: 'By element (gray carbons)', value: ElementColorCode },
    { label: 'By secondary structure', value: SecondaryStructureColorCode },
];

/** Transparency choices of a row (style_manager RepSetTransparencies). */
export const AdvancedStyleTransparencies = RepSetTransparencies.map((value) => ({
    value, label: `${Math.round(value * 100)}%`,
}));

/** Cartoon type choices of a row (style_manager RepSetCartoonTypes). */
export const AdvancedStyleCartoonTypes = RepSetCartoonTypes.map((value) => ({
    value, label: value[0].toUpperCase() + value.slice(1),
}));

export const AllRowId = '*';

/** Color choices of a color-only row (hotspots are clouds: one color, no scheme). */
export const AdvancedStylePaletteColors = Object.entries(ColorChain)
    .map(([label, value]) => ({ label, value }));

/** Row kinds with a color only: no reps, labels or rep-set options. */
const ColorOnlyKinds = ['hotspots'];

/** Sub-rows of a chain row: [row id suffix, scope, label suffix]. */
const ScopeRows = [['/main', 'mainchain', 'main chain'], ['/side', 'sidechain', 'side chains']];

/** @returns {*} the one value all of `values` share, else null */
function common(values) {
    const set = new Set(values);
    return set.size === 1 ? [...set][0] : null;
}

export { repsForMolStyle }; // moved to style_manager (display_mgr needs it too)

const ChainTypes = [AtomGroupTypes.Protein, AtomGroupTypes.Polymer];
const WaterTypes = [AtomGroupTypes.CrystalWater, AtomGroupTypes.ComputedWater];
const HeteroTypes = [
    AtomGroupTypes.Ion, AtomGroupTypes.Cofactor, AtomGroupTypes.Buffer, AtomGroupTypes.Ligand,
    AtomGroupTypes.PeptideLigand, AtomGroupTypes.Unknown,
];

/** Row kinds whose groups draw a label (waters draw none). */
const LabelKinds = ['all', 'chain', 'compound', 'fragments', 'hetero'];

/** @returns {'none'|'some'|'all'} */
function stateOf(count, total) {
    if (count === 0) return 'none';
    return count >= total ? 'all' : 'some';
}

export class AdvancedStylesModel {
    /**
     * @param {object} deps
     * @param {object} deps.workspace App.Workspace: allAtomGroups, getVisibleCompounds,
     *     getDefaultAtomGroupStyle, getAllHotspots (optional)
     * @param {object} deps.userActions UserActions: SetCustomAtomGroupStyle, RedrawScene
     * @param {object} deps.styleManager StyleManager: getCustomAtomGroupStyle
     * @param {() => string[]} deps.supportedReps the reps the current viewer draws
     * @param {() => string[]} [deps.transparentReps] the reps it makes transparent per group
     */
    constructor({
        workspace, userActions, styleManager, supportedReps,
        transparentReps=() => ['sticks', 'spheres', 'cartoon', 'surface'],
    }) {
        Object.assign(this, {
            workspace, userActions, styleManager, supportedReps, transparentReps,
        });
    }

    /**
     * @returns {{id: string, label: string, kind: string, groups: object[], scope?: string,
     *            part?: string}[]} `scope` / `part`: a chain's main chain / side chains sub-row
     */
    rows() {
        const groups = this.workspace.allAtomGroups();
        const compounds = new Set(this.workspace.getVisibleCompounds(true) || []);
        const rows = [];
        const add = (id, label, kind, members) => {
            if (members.length > 0) {
                rows.push({
                    id, label, kind, groups: members,
                });
            }
        };
        groups.filter((g) => ChainTypes.includes(g.type)).forEach((g, i) => {
            const id = `chain-${g.key || i}`;
            const label = g.displayName || `Chain ${i + 1}`;
            add(id, label, 'chain', [g]);
            for (const [suffix, scope, part] of ScopeRows) {
                rows.push({
                    id: id + suffix, label: `${label} ${part}`, part, kind: scope, scope, groups: [g],
                });
            }
        });
        groups.filter((g) => compounds.has(g))
            .forEach((g, i) => add(`compound-${g.key || i}`, g.displayName || `Compound ${i + 1}`, 'compound', [g]));
        add('waters', 'Waters', 'waters', groups.filter((g) => WaterTypes.includes(g.type)));
        add('fragments', 'Fragments', 'fragments', groups.filter((g) => g.type === AtomGroupTypes.Fragment));
        add('hetero', 'Other hetero groups', 'hetero', groups.filter((g) => HeteroTypes.includes(g.type)));
        const topLevel = rows.filter((r) => !r.scope);
        // hotspots: a color only, so not part of All
        const hotspots = this.workspace.getAllHotspots ? this.workspace.getAllHotspots() || [] : [];
        add('hotspots', 'Hotspots', 'hotspots', hotspots);
        if (topLevel.length > 1) {
            rows.unshift({
                id: AllRowId, label: 'All', kind: 'all', groups: topLevel.flatMap((r) => r.groups),
            });
        }
        return rows;
    }

    /** The reps a group shows now: its rep set, else those of its single style. */
    effectiveReps(group) {
        const custom = this.styleManager.getCustomAtomGroupStyle(group) || {};
        if (Array.isArray(custom.reps)) return custom.reps;
        return repsForMolStyle(custom.molStyle || this.workspace.getDefaultAtomGroupStyle(group));
    }

    /** The reps one part of a chain shows: its own set, else the chain's. */
    scopeReps(group, scope) {
        const scoped = (this.styleManager.getCustomAtomGroupStyle(group) || {}).scopedReps;
        return (scoped && scoped[scope]) || this.effectiveReps(group);
    }

    /** What a row's checkboxes cover: one part for a sub-row; for a chain with parts, both. */
    unitsOf(row) {
        if (row.scope) return row.groups.map((g) => this.scopeReps(g, row.scope));
        return row.groups.flatMap((g) => {
            const { scopedReps } = this.styleManager.getCustomAtomGroupStyle(g) || {};
            return scopedReps
                ? ['mainchain', 'sidechain'].map((scope) => this.scopeReps(g, scope))
                : [this.effectiveReps(g)];
        });
    }

    /**
     * The table as the UI draws it.
     * @returns {{rows: {id, label, kind, reps: Object<string, 'none'|'some'|'all'>, color: *,
     *                   transparency: number|null, cartoonType: string|null}[],
     *            available: Object<string, boolean>, transparentReps: string[]}}
     *     null = the row's groups differ
     */
    read() {
        const supported = new Set(this.supportedReps());
        const available = Object.fromEntries(RepNames.map((name) => [name, supported.has(name)]));
        const rows = this.rows().map((row) => {
            const units = this.unitsOf(row);
            const reps = Object.fromEntries(RepNames.map((name) => [
                name, stateOf(units.filter((r) => r.includes(name)).length, units.length),
            ]));
            const custom = row.groups
                .map((g) => this.styleManager.getCustomAtomGroupStyle(g) || {});
            if (ColorOnlyKinds.includes(row.kind)) {
                return {
                    id: row.id,
                    label: row.label,
                    kind: row.kind,
                    colorOnly: true,
                    color: common(custom.map((c) => c.color ?? DefaultColorCode)),
                };
            }
            if (row.scope) {
                // color and how reps draw belong to the whole chain
                const {
                    id, label, part, kind, scope,
                } = row;
                return {
                    id, label, part, kind, scope, reps,
                };
            }
            const labels = LabelKinds.includes(row.kind)
                ? stateOf(custom.filter((c) => c.showLabel).length, custom.length) : null;
            return {
                id: row.id,
                label: row.label,
                kind: row.kind,
                reps,
                labels,
                color: common(custom.map((c) => c.color ?? DefaultColorCode)),
                // one value for every rep; null when per rep or the groups differ
                transparency: common(custom.map((c) => (typeof (c.transparency ?? 0) === 'number'
                    ? c.transparency ?? 0 : null))),
                transparencyByRep: Object.fromEntries(TransparentRepNames.map((rep) => [
                    rep, common(custom.map((c) => transparencyByRep(c.transparency)[rep])),
                ])),
                cartoonType: common(custom.map((c) => c.cartoonType ?? 'automatic')),
            };
        });
        return { rows, available, transparentReps: this.transparentReps() };
    }

    findRow(rowId) {
        const row = this.rows().find((r) => r.id === rowId);
        if (!row) throw new Error(`No such row: ${rowId}`);
        return row;
    }

    /** A row that has reps (not a color-only one). */
    findStyleRow(rowId) {
        const row = this.findRow(rowId);
        if (ColorOnlyKinds.includes(row.kind)) throw new Error(`${row.label} have a color only`);
        return row;
    }

    /**
     * Show a representation on every group of a row, or - when all of them show it already -
     * hide it on all of them. Each group gets a rep set built from what it shows now.
     */
    toggle(rowId, rep) {
        if (!RepNames.includes(rep)) throw new Error(`Unknown representation: ${rep}`);
        const row = this.findStyleRow(rowId);
        const all = this.unitsOf(row).every((reps) => reps.includes(rep));
        const flip = (reps) => (all ? reps.filter((r) => r !== rep) : [...reps, rep]);
        for (const group of row.groups) {
            const { scopedReps } = this.styleManager.getCustomAtomGroupStyle(group) || {};
            if (row.scope) {
                const next = flip(this.scopeReps(group, row.scope));
                const scoped = { ...scopedReps, [row.scope]: next };
                this.userActions.SetCustomAtomGroupStyle(group, { scopedReps: scoped });
            } else if (scopedReps) {
                // the whole chain: its parts with their own sets too
                const scoped = Object.fromEntries(Object.entries(scopedReps)
                    .map(([scope, reps]) => [scope, flip(reps)]));
                this.userActions.SetCustomAtomGroupStyle(group, {
                    reps: flip(this.effectiveReps(group)), scopedReps: scoped,
                });
            } else {
                const reps = flip(this.effectiveReps(group));
                this.userActions.SetCustomAtomGroupStyle(group, { reps });
            }
        }
        this.userActions.RedrawScene();
    }

    /**
     * Show the row's labels (a chain: one per residue), or - when all its groups show theirs -
     * hide them. Rows without labels (waters, a chain's parts) refuse.
     */
    toggleLabels(rowId) {
        const row = this.findRow(rowId);
        if (!LabelKinds.includes(row.kind)) throw new Error(`No labels for ${row.label}`);
        const all = row.groups
            .every((g) => (this.styleManager.getCustomAtomGroupStyle(g) || {}).showLabel);
        for (const group of row.groups) {
            this.userActions.SetCustomAtomGroupStyle(group, { showLabel: !all });
        }
        this.userActions.RedrawScene();
    }

    /** @param {*} color a ColorChain value ('default' clears the group color) */
    setColor(rowId, color) {
        const row = this.findRow(rowId);
        if (ColorOnlyKinds.includes(row.kind)
            && !AdvancedStylePaletteColors.some((c) => c.value === color)) {
            throw new Error(`${row.label} take one color, not a scheme`);
        }
        for (const group of row.groups) {
            this.userActions.SetCustomAtomGroupStyle(group, { color });
        }
        this.userActions.RedrawScene();
    }

    /**
     * How the row's reps draw. These only apply to a rep set, so a group without one gets a rep
     * set of what it shows now.
     */
    setRepSetOption(rowId, option) {
        for (const group of this.findStyleRow(rowId).groups) {
            const custom = this.styleManager.getCustomAtomGroupStyle(group) || {};
            const reps = Array.isArray(custom.reps) ? {} : { reps: this.effectiveReps(group) };
            const value = typeof option === 'function' ? option(custom) : option;
            this.userActions.SetCustomAtomGroupStyle(group, { ...reps, ...value });
        }
        this.userActions.RedrawScene();
    }

    /**
     * @param {number} transparency one of RepSetTransparencies
     * @param {string} [rep] one of TransparentRepNames: that rep only; else every rep
     */
    setTransparency(rowId, transparency, rep) {
        if (!RepSetTransparencies.includes(transparency)) throw new Error(`Unknown transparency: ${transparency}`);
        if (rep === undefined) {
            this.setRepSetOption(rowId, { transparency });
            return;
        }
        if (!TransparentRepNames.includes(rep)) throw new Error(`No transparency for ${rep}`);
        this.setRepSetOption(rowId, (custom) => ({
            transparency: { ...transparencyByRep(custom.transparency), [rep]: transparency },
        }));
    }

    /** @param {string} cartoonType one of RepSetCartoonTypes */
    setCartoonType(rowId, cartoonType) {
        if (!RepSetCartoonTypes.includes(cartoonType)) throw new Error(`Unknown cartoon type: ${cartoonType}`);
        this.setRepSetOption(rowId, { cartoonType });
    }

    /**
     * Back to BMaps' own styles for the row: no rep set (nor its options or parts), no custom
     * color. For a sub-row: the part draws the chain's reps again.
     */
    reset(rowId) {
        const row = this.findRow(rowId);
        for (const group of row.groups) {
            if (row.scope) {
                const { scopedReps } = this.styleManager.getCustomAtomGroupStyle(group) || {};
                const scoped = { ...scopedReps };
                delete scoped[row.scope];
                this.userActions.SetCustomAtomGroupStyle(group, { scopedReps: scoped });
            } else {
                this.userActions.SetCustomAtomGroupStyle(group, {
                    reps: 'default', color: DefaultColorCode, showLabel: 'default',
                });
            }
        }
        this.userActions.RedrawScene();
    }
}
